import React from "react";
import Flatpickr from "react-flatpickr";
import { AlertCircle } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import { SERIES_FLATPICKR, tagSeriesCalendar } from "../seriesFlatpickr";
import { offsetDateForBrowser, offsetDateForServer } from "../../../shared/timeZoneLogic";
import TimeField from "./TimeField";

const DEFAULT_CLOCK = "09:00";

const OPTIONS = [
  { scheduled: false, label: "Publish all now", description: "All shows go live as soon as you create the series." },
  {
    scheduled: true,
    label: "Publish all on a schedule",
    description: "All shows go live together at the date and time you pick.",
  },
];

const toClock = (date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

// ponytail: "schedule" with no date yet lives only in the form, so a reload
// before a date is picked reopens on "now". Nothing publishes early: the
// missing date blocks Create & Publish.
export const publishOnSchedule = (form) => form.publishOnSchedule ?? !!form.showsPublishAt;

export const publishingProblems = (form) => {
  if (form.isDraft === false || !publishOnSchedule(form)) return [];
  if (!form.showsPublishAt) return ["Choose a publish date"];
  if (new Date(form.showsPublishAt) <= new Date()) return ["Publish date is in the past"];
  return [];
};

const PublishingCard = () => {
  const { form, setField } = useSeriesEditContext();
  const timeZone = form.venueTimeZone;
  const scheduled = publishOnSchedule(form);
  const locked = form.isDraft === false;
  const local = offsetDateForBrowser(form.showsPublishAt, timeZone);
  const clock = form.publishClock || (local ? toClock(local) : DEFAULT_CLOCK);

  const setPublishAt = (day, time) => {
    if (!day) return setField("showsPublishAt", null);
    const [hours, minutes] = time.split(":").map(Number);
    const wallClock = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hours, minutes);
    setField("showsPublishAt", offsetDateForServer(wallClock, timeZone).toISOString());
  };

  const choose = (option) => {
    setField("publishOnSchedule", option.scheduled);
    if (!option.scheduled) setField("showsPublishAt", null);
  };

  return (
    <section className="series-edit-card series-publishing">
      <h3 className="series-publishing-title">Publishing</h3>
      <p className="series-edit-section-subtitle">
        Choose when this series goes live on your website, widget and Opendate listings.
      </p>

      <div className="series-mode-grid" role="radiogroup" aria-label="Publishing">
        {OPTIONS.map((option) => (
          <button
            key={option.label}
            type="button"
            role="radio"
            aria-checked={scheduled === option.scheduled}
            disabled={locked}
            className={["series-mode-option", scheduled === option.scheduled ? "selected" : ""].join(" ").trim()}
            onClick={() => choose(option)}
          >
            <span className="series-mode-option-head">
              <span className="series-mode-radio" aria-hidden="true" />
              <span className="series-mode-option-label">{option.label}</span>
            </span>
            <span className="series-mode-option-description">{option.description}</span>
          </button>
        ))}
      </div>

      {scheduled && (
        <div className="series-publishing-fields">
          <label className="series-edit-label">
            Publish Date
            <Flatpickr
              className="series-edit-input"
              options={{ ...SERIES_FLATPICKR, dateFormat: "M j, Y" }}
              value={local || ""}
              placeholder="Select date"
              aria-label="Publish Date"
              disabled={locked}
              onChange={([day]) => setPublishAt(day, clock)}
              onReady={tagSeriesCalendar}
            />
          </label>
          <label className="series-edit-label">
            Publish Time
            <TimeField
              value={clock}
              ariaLabel="Publish Time"
              disabled={locked}
              onChange={(time) => {
                if (!time) return;
                setField("publishClock", time);
                if (local) setPublishAt(local, time);
              }}
            />
          </label>
        </div>
      )}

      <p className="series-publishing-note">
        <AlertCircle width={16} height={16} aria-hidden="true" />
        Publishing makes shows visible. Ticket sales still start at each show&apos;s Public On-Sale time.
      </p>
    </section>
  );
};

export default PublishingCard;
